import React, { useEffect, useState } from "react";
import { Button, Card, Col, message, Row, Space, Tag, Typography } from "antd";
import { ReloadOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import DashboardLayout from "../components/DashboardLayout";
import CustomAvatar from "../components/CustomAvatar";
import { AvatarConfig, DEFAULT_AVATAR, ACCESSORY_ITEMS } from "../types/user";

const AVATAR_OPTIONS = [
  { id: 0, name: "Avatar 1" },
  { id: 1, name: "Avatar 2" },
  { id: 2, name: "Avatar 3" },
  { id: 3, name: "Avatar 4" },
  { id: 4, name: "Avatar 5" },
];

const AvatarCustomization: React.FC = () => {
  const [avatarConfig, setAvatarConfig] =
    useState<AvatarConfig>(DEFAULT_AVATAR);
  const [savedMessage, setSavedMessage] = useState("");

  useEffect(() => {
    const savedAvatar = localStorage.getItem("userAvatar");
    if (!savedAvatar) return;

    try {
      setAvatarConfig(JSON.parse(savedAvatar));
    } catch (error) {
      console.error("Error loading saved avatar:", error);
    }
  }, []);

  const updateAvatar = (base: number) => {
    setAvatarConfig((prev) => ({ ...prev, base }));
  };

  const removeAccessory = (type: "hat" | "clothing" | "hairstyle") => {
    setAvatarConfig((prev) => ({
      ...prev,
      accessories: { ...prev.accessories, [type]: undefined },
    }));
  };

  const saveAvatar = () => {
    localStorage.setItem("userAvatar", JSON.stringify(avatarConfig));
    setSavedMessage("¡Avatar guardado correctamente!");
    message.success("Avatar guardado correctamente");
  };

  const resetAvatar = () => {
    setAvatarConfig(DEFAULT_AVATAR);
    setSavedMessage("Avatar restablecido");
    message.info("Avatar restablecido");
  };

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1440, margin: "0 auto", width: "100%" }}>
        <Card
          className="app-glass"
          style={{
            borderRadius: 24,
            border: "1px solid var(--dashboard-panel-border)",
            boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Typography.Text
              strong
              style={{
                color: "var(--dashboard-header-blue)",
                textTransform: "uppercase",
                letterSpacing: 1.2,
              }}
            >
              Avatar
            </Typography.Text>
            <Typography.Title
              level={2}
              style={{ margin: 0, color: "var(--dashboard-header-blue)" }}
            >
              Personalizar avatar
            </Typography.Title>
            <Typography.Text style={{ color: "var(--dashboard-text)" }}>
              Conserva la misma información y cambia entre bases, accesorios y
              acciones rápidas.
            </Typography.Text>
          </Space>
        </Card>

        {savedMessage ? (
          <Card
            style={{
              marginTop: 16,
              borderRadius: 18,
              border: "1px solid var(--dashboard-teal)",
              background: "rgba(79,194,134,0.14)",
            }}
            bodyStyle={{ padding: 14 }}
          >
            <Typography.Text strong style={{ color: "#1f8f62" }}>
              {savedMessage}
            </Typography.Text>
          </Card>
        ) : null}

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={10}>
            <Card
              className="app-glass"
              style={{
                borderRadius: 24,
                border: "1px solid var(--dashboard-panel-border)",
                boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
              }}
              bodyStyle={{ padding: 20 }}
            >
              <Space
                direction="vertical"
                size={16}
                style={{ width: "100%", alignItems: "center" }}
              >
                <div style={{ textAlign: "center" }}>
                  <Typography.Title
                    level={4}
                    style={{ margin: 0, color: "var(--dashboard-header-blue)" }}
                  >
                    Vista previa
                  </Typography.Title>
                  <Typography.Text style={{ color: "var(--dashboard-text)" }}>
                    El avatar se mantiene responsive y legible.
                  </Typography.Text>
                </div>

                <CustomAvatar config={avatarConfig} size={180} fullBody />

                <Space wrap>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={saveAvatar}
                    style={{
                      borderRadius: 14,
                      fontWeight: 800,
                      background: "var(--dashboard-yellow)",
                      borderColor: "var(--dashboard-yellow-dark)",
                      color: "var(--dashboard-header-blue)",
                    }}
                  >
                    Guardar
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={resetAvatar}
                    style={{ borderRadius: 14, fontWeight: 800 }}
                  >
                    Restablecer
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card
                className="app-glass"
                style={{
                  borderRadius: 24,
                  border: "1px solid var(--dashboard-panel-border)",
                  boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
                }}
                bodyStyle={{ padding: 20 }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Typography.Title
                    level={4}
                    style={{ margin: 0, color: "var(--dashboard-header-blue)" }}
                  >
                    Elegí una base
                  </Typography.Title>
                  <Row gutter={[12, 12]}>
                    {AVATAR_OPTIONS.map((option) => {
                      const selected = avatarConfig.base === option.id;
                      return (
                        <Col xs={12} sm={8} key={option.id}>
                          <Card
                            hoverable
                            onClick={() => updateAvatar(option.id)}
                            style={{
                              borderRadius: 18,
                              border: `2px solid ${selected ? "var(--dashboard-yellow-dark)" : "rgba(47,43,116,0.12)"}`,
                              background: selected
                                ? "linear-gradient(135deg, rgba(247,197,11,0.24), rgba(255,255,255,0.9))"
                                : "rgba(255,255,255,0.78)",
                            }}
                            bodyStyle={{ padding: 14, textAlign: "center" }}
                          >
                            <CustomAvatar
                              config={{ ...DEFAULT_AVATAR, base: option.id }}
                              size={68}
                            />
                            <div style={{ marginTop: 10 }}>
                              <Typography.Text
                                strong
                                style={{
                                  color: selected
                                    ? "var(--dashboard-header-blue)"
                                    : "var(--dashboard-text)",
                                }}
                              >
                                {option.name}
                              </Typography.Text>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </Space>
              </Card>

              <Card
                className="app-glass"
                style={{
                  borderRadius: 24,
                  border: "1px solid var(--dashboard-panel-border)",
                  boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
                }}
                bodyStyle={{ padding: 20 }}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Typography.Title
                    level={4}
                    style={{ margin: 0, color: "var(--dashboard-header-blue)" }}
                  >
                    Accesorios equipados
                  </Typography.Title>
                  <Row gutter={[12, 12]}>
                    {(["hat", "clothing", "hairstyle"] as const).map((type) => {
                      const equippedItem = avatarConfig.accessories[type];
                      const accessory = equippedItem
                        ? ACCESSORY_ITEMS.find(
                            (item) => item.id === equippedItem,
                          )
                        : null;

                      return (
                        <Col xs={24} sm={8} key={type}>
                          <Card
                            style={{
                              borderRadius: 18,
                              border: `1px solid ${accessory ? "var(--dashboard-teal)" : "rgba(47,43,116,0.12)"}`,
                              background: accessory
                                ? "rgba(79,194,134,0.14)"
                                : "rgba(255,255,255,0.8)",
                            }}
                            bodyStyle={{
                              padding: 14,
                              textAlign: "center",
                              minHeight: 126,
                            }}
                          >
                            {accessory ? (
                              <Space direction="vertical" size={8}>
                                <div style={{ fontSize: 32 }}>
                                  {accessory.icon}
                                </div>
                                <Tag color="green">{accessory.name}</Tag>
                                <Button
                                  size="small"
                                  icon={<CloseOutlined />}
                                  onClick={() => removeAccessory(type)}
                                >
                                  Quitar
                                </Button>
                              </Space>
                            ) : (
                              <Typography.Text
                                style={{ color: "var(--dashboard-text)" }}
                              >
                                {type === "hat"
                                  ? "Sombrero"
                                  : type === "clothing"
                                    ? "Ropa"
                                    : "Peinado"}
                              </Typography.Text>
                            )}
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </Space>
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </DashboardLayout>
  );
};

export default AvatarCustomization;
